import { useEffect, useMemo } from 'react'
import { Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { tenantToday } from '@/shared/lib/dayRange'
import { getCurrencyCode } from '@/shared/lib/format'
import { formatMoneyValue, money, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { withCurrentOption } from '@/shared/ui/currentOption'
import { costCenterDepths, fetchAccounts, fetchCostCenters } from '@/features/finance/financeApi'
import { canReceiveStock, fetchStockItems, fetchWarehouses } from '@/features/inventory/stockApi'
import {
  LINE_KINDS,
  fetchActiveSuppliers,
  lineAmountMinor,
  savePurchase,
  type LineKind,
  type Purchase,
} from './supplierApi'

interface PurchaseFormModalProps {
  open: boolean
  purchase: Purchase | null
  presetSupplierId?: string | null
  onClose: () => void
  onSaved: (purchase: Purchase) => void
}

interface LineValues {
  kind: LineKind
  stockItemId?: string
  accountId?: string
  description: string
  quantity: number
  unitPrice: Money | null
}

interface FormValues {
  supplierId: string
  invoiceNo?: string
  invoiceDate: Dayjs
  dueDate?: Dayjs | null
  orgNodeId?: string | null
  warehouseNodeId?: string | null
  memo?: string
  lines: LineValues[]
}

const INDENT = '  '
const SERVICE_ACCOUNT_TYPES = ['EXPENSE', 'ASSET']

export function PurchaseFormModal({ open, purchase, presetSupplierId, onClose, onSaved }: PurchaseFormModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  const suppliers = useQuery({
    queryKey: queryKeys.supplier.active,
    queryFn: ({ signal }) => fetchActiveSuppliers(signal),
    enabled: open,
  })
  const warehouses = useQuery({ queryKey: queryKeys.stock.warehouses, queryFn: fetchWarehouses, enabled: open })
  const items = useQuery({
    queryKey: queryKeys.stock.items(true),
    queryFn: () => fetchStockItems(true),
    enabled: open,
  })
  const accounts = useQuery({
    queryKey: queryKeys.finance.accounts(true),
    queryFn: ({ signal }) => fetchAccounts(true, signal),
    enabled: open,
  })
  const costCenters = useQuery({
    queryKey: queryKeys.finance.costCenters,
    queryFn: ({ signal }) => fetchCostCenters(signal),
    enabled: open,
  })

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      supplierId: purchase?.supplier.id ?? presetSupplierId ?? undefined,
      invoiceNo: purchase?.invoiceNo ?? '',
      invoiceDate: purchase ? dayjs(purchase.invoiceDate) : tenantToday(),
      dueDate: purchase?.dueDate ? dayjs(purchase.dueDate) : null,
      orgNodeId: purchase?.orgNode?.id ?? null,
      warehouseNodeId: purchase?.warehouse?.id ?? null,
      memo: purchase?.memo ?? '',
      lines: purchase
        ? purchase.lines.map((line) => ({
            kind: line.kind,
            stockItemId: line.stockItemId ?? undefined,
            accountId: line.accountId ?? undefined,
            description: line.description,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
          }))
        : [{ kind: 'STOCK', description: '', quantity: 1, unitPrice: null }],
    })
  }, [open, purchase, presetSupplierId, form])

  const supplierOptions = (suppliers.data ?? []).map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))
  const warehouseOptions = withCurrentOption(
    (warehouses.data ?? []).filter(canReceiveStock).map((w) => ({ value: w.id, label: `${w.displayName} (${w.code})` })),
    purchase?.warehouse?.id,
    (id) =>
      purchase?.warehouse?.id === id
        ? { value: id, label: `${purchase.warehouse.name} (${purchase.warehouse.code})`, disabled: true }
        : null,
  )
  const itemOptions = (items.data ?? []).map((item) => ({ value: item.id, label: `${item.name} · ${item.sku}` }))
  const accountOptions = (accounts.data ?? [])
    .filter((a) => a.postable && a.active && SERVICE_ACCOUNT_TYPES.includes(a.type))
    .map((a) => ({ value: a.id, label: `${a.code} · ${seededName('accounts', a.code, a.name, t)}` }))
  const resolveLineItem = (id: string) => {
    const line = purchase?.lines.find((candidate) => candidate.stockItemId === id)
    return line ? { value: id, label: line.description, disabled: true } : null
  }
  const resolveLineAccount = (id: string) => {
    const line = purchase?.lines.find((candidate) => candidate.accountId === id)
    return line?.account ? { value: id, label: `${line.account.code} · ${seededName('accounts', line.account.code, line.account.name, t)}`, disabled: true } : null
  }
  const centerOptions = useMemo(() => {
    const rows = costCenters.data ?? []
    const depths = costCenterDepths(rows)
    return rows.map((node) => ({
      value: node.id,
      label: `${INDENT.repeat(depths.get(node.id) ?? 0)}${node.displayName} · ${node.code}`,
    }))
  }, [costCenters.data])

  const lines = Form.useWatch('lines', form) ?? []
  const total = lines.reduce((sum, line) => sum + lineAmountMinor(line?.quantity, line?.unitPrice?.amount), 0)
  const hasStock = lines.some((line) => line?.kind === 'STOCK')

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      savePurchase(purchase?.id ?? null, {
        supplierId: values.supplierId,
        invoiceNo: values.invoiceNo?.trim() ? values.invoiceNo.trim() : null,
        invoiceDate: values.invoiceDate.format('YYYY-MM-DD'),
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,
        orgNodeId: values.orgNodeId ?? null,
        warehouseNodeId: values.lines.some((line) => line.kind === 'STOCK') ? (values.warehouseNodeId ?? null) : null,
        memo: values.memo?.trim() ? values.memo.trim() : null,
        lines: values.lines.map((line) => ({
          kind: line.kind,
          stockItemId: line.kind === 'STOCK' ? (line.stockItemId ?? null) : null,
          accountId: line.kind === 'SERVICE' ? (line.accountId ?? null) : null,
          description: line.description.trim(),
          quantity: line.quantity,
          unitPriceMinor: line.unitPrice?.amount ?? 0,
        })),
      }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.supplier.all })
      onSaved(saved)
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const fillDescription = (index: number, itemId: string) => {
    const item = items.data?.find((candidate) => candidate.id === itemId)
    const current = form.getFieldValue(['lines', index, 'description']) as string | undefined
    if (item && !current?.trim()) {
      form.setFieldValue(['lines', index, 'description'], item.name)
    }
  }

  return (
    <Modal
      open={open}
      title={purchase ? t('purchase.form.editTitle', { number: purchase.number }) : t('purchase.form.createTitle')}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      onCancel={close}
      confirmLoading={save.isPending}
      destroyOnHidden
      width={980}
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('purchase.form.note')} />
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item name="supplierId" label={t('purchase.field.supplier')} rules={[{ required: true, message: t('purchase.validation.supplier') }]}>
              <Select showSearch optionFilterProp="label" options={supplierOptions} loading={suppliers.isPending} />
            </Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item name="invoiceNo" label={t('purchase.field.invoiceNo')}>
              <Input maxLength={64} />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item name="invoiceDate" label={t('purchase.field.invoiceDate')} rules={[{ required: true, message: t('purchase.validation.invoiceDate') }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={5}>
            <Form.Item name="dueDate" label={t('purchase.field.dueDate')} extra={t('purchase.field.dueDateHint')}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="orgNodeId" label={t('purchase.field.orgNode')} extra={t('purchase.field.orgNodeHint')}>
              <Select allowClear showSearch optionFilterProp="label" options={centerOptions} loading={costCenters.isPending} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="warehouseNodeId"
              label={t('purchase.field.warehouse')}
              rules={hasStock ? [{ required: true, message: t('purchase.validation.warehouse') }] : []}
            >
              <Select allowClear showSearch optionFilterProp="label" options={warehouseOptions} disabled={!hasStock} />
            </Form.Item>
          </Col>
        </Row>

        <Typography.Title level={5}>{t('purchase.field.lines')}</Typography.Title>
        <Form.List name="lines" rules={[{ validator: async (_, value: unknown[]) => (value?.length ? Promise.resolve() : Promise.reject(new Error(t('purchase.validation.lines')))) }]}>
          {(fields, { add, remove }, { errors }) => (
            <>
              <Row gutter={8}>
                {[
                  { md: 3, label: t('purchase.field.kind') },
                  { md: 6, label: `${t('purchase.field.item')} / ${t('purchase.field.account')}` },
                  { md: 6, label: t('purchase.field.description') },
                  { md: 2, label: t('purchase.field.quantity') },
                  { md: 4, label: t('purchase.field.unitPrice') },
                  { md: 2, label: t('purchase.field.amount') },
                ].map((column) => (
                  <Col key={column.label} xs={0} md={column.md}>
                    <Typography.Text type="secondary">{column.label}</Typography.Text>
                  </Col>
                ))}
              </Row>
              {fields.map((field, index) => {
                const kind: LineKind = lines[index]?.kind ?? 'STOCK'
                const amount = lineAmountMinor(lines[index]?.quantity, lines[index]?.unitPrice?.amount)
                return (
                  <Row gutter={8} key={field.key} align="top">
                    <Col xs={24} md={3}>
                      <Form.Item name={[field.name, 'kind']} rules={[{ required: true, message: t('purchase.validation.kind') }]}>
                        <Select options={LINE_KINDS.map((value) => ({ value, label: t(`purchase.lineKind.${value}`) }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      {kind === 'STOCK' ? (
                        <Form.Item name={[field.name, 'stockItemId']} rules={[{ required: true, message: t('purchase.validation.item') }]}>
                          <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder={t('purchase.field.item')}
                            options={withCurrentOption(itemOptions, lines[index]?.stockItemId, resolveLineItem)}
                            onChange={(value: string) => fillDescription(index, value)}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item name={[field.name, 'accountId']} rules={[{ required: true, message: t('purchase.validation.account') }]}>
                          <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder={t('purchase.field.account')}
                            options={withCurrentOption(accountOptions, lines[index]?.accountId, resolveLineAccount)}
                          />
                        </Form.Item>
                      )}
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item name={[field.name, 'description']} rules={[{ required: true, whitespace: true, message: t('purchase.validation.description') }]}>
                        <Input maxLength={300} placeholder={t('purchase.field.description')} />
                      </Form.Item>
                    </Col>
                    <Col xs={8} md={2}>
                      <Form.Item name={[field.name, 'quantity']} rules={[{ required: true, message: t('purchase.validation.quantity') }]}>
                        <InputNumber min={1} max={1_000_000_000} precision={0} style={{ width: '100%' }} placeholder={t('purchase.field.quantity')} />
                      </Form.Item>
                    </Col>
                    <Col xs={16} md={4}>
                      <Form.Item name={[field.name, 'unitPrice']} rules={[{ required: true, message: t('purchase.validation.unitPrice') }]}>
                        <MoneyInput currency={currency} minMinor={0} placeholder={t('purchase.field.unitPrice')} />
                      </Form.Item>
                    </Col>
                    <Col xs={20} md={2} style={{ paddingTop: 5 }}>
                      <Typography.Text>{formatMoneyValue(money(amount, currency))}</Typography.Text>
                    </Col>
                    <Col xs={4} md={1}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t('purchase.form.removeLine')}
                        disabled={fields.length === 1}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  </Row>
                )
              })}
              <Form.ErrorList errors={errors} />
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button icon={<PlusOutlined />} onClick={() => add({ kind: 'STOCK', description: '', quantity: 1, unitPrice: null })}>
                  {t('purchase.form.addLine')}
                </Button>
                <Typography.Text strong>
                  {t('purchase.field.total')}: {formatMoneyValue(money(total, currency))}
                </Typography.Text>
              </Space>
            </>
          )}
        </Form.List>

        <Form.Item name="memo" label={t('purchase.field.memo')} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
